import type { ReactNode } from "react";
import {
	BrowserRouter as Router,
	Routes,
	Route,
	Navigate,
	useLocation,
	useParams,
} from "react-router-dom";
import { ConfigProvider, theme } from "antd";
import { Toaster } from "sonner";
import { useBrandTokens } from "./hooks/useBrandTokens";
import { useTheme } from "./components/theme/ThemeProvider";
import { PrePublishGateProvider } from "./providers/PrePublishGateProvider";
import ModernMainLayout from "./layouts/ModernMainLayout";
import { AuthProvider } from "./contexts/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";
import ProtectedRoute from "./components/ProtectedRoute";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import PasswordResetPage from "./pages/PasswordResetPage";
import CreateWorkspacePage from "./pages/CreateWorkspacePage";
import OnboardingPage from "./pages/onboarding/OnboardingPage";
import AuthCallback from "./pages/AuthCallback";
import { ClientSettingsLayout } from "./components/settings/ClientSettingsLayout";
import BusinessInfoSection from "./pages/settings/BusinessInfoSection";
import IntegrationsSection from "./pages/settings/IntegrationsSection";
import ContentSection from "./pages/settings/ContentSection";
import WebsiteSection from "./pages/settings/WebsiteSection";
import MediaSection from "./pages/settings/MediaSection";
import TeamSection from "./pages/settings/TeamSection";
import { SETTINGS_REDIRECT_ROUTES } from "./pages/settings/redirectRoutes";
import { PLATFORM_REDIRECT_ROUTES } from "./pages/platform/redirectRoutes";
import TopPagesPage from "./pages/overview/TopPagesPage";
import TrafficPage from "./pages/overview/TrafficPage";
import SiteHealthPage from "./pages/overview/SiteHealthPage";
import ConnectSearchConsolePage from "./pages/connect/ConnectSearchConsolePage";
import ConnectGA4Page from "./pages/connect/ConnectGA4Page";
import BrandBiblePage from "./pages/BrandBiblePage";
import CrewsPage from "./pages/CrewsPage";
import AgentsPage from "./pages/AgentsPage";
import TasksPage from "./pages/TasksPage";
import ToolsPage from "./pages/ToolsPage";
import UsagePage from "./pages/UsagePage";
import UsageCreditsPage from "./pages/UsageCreditsPage";
import CostAttributionPage from "./pages/CostAttributionPage";
import RunPage from "./pages/RunPage";
import ResultsPage from "./pages/ResultsPage";
import ContentPlannerWizardPage from "./pages/content/ContentPlannerWizardPage";
import {
	BlogTemplatesPage,
	ContentPlannerPage,
	BlogDetailPage,
	UniversalEditorPage,
	ClusterResultsPage,
	OutlineReviewPage,
	BenchmarkDashboardPage,
	SectionReviewPage,
} from "./pages/content";
import QAReviewPage from "./pages/QAReviewPage";
import QAContentChecksPage from "./pages/QAContentChecksPage";
import HumanQaReviewWorkbenchPage from "./pages/content/HumanQaReviewWorkbenchPage";
import ProposalsPage from "./pages/ProposalsPage";
import { FEATURE_FLAGS } from "./config/environment";
import ComprehensiveSiteAuditPage from "./pages/ComprehensiveSiteAuditPage";
import SchemaManagementPage from "./pages/SchemaManagementPage";
import TechnicalAuditIssuesPage from "./pages/TechnicalAuditIssuesPage";
import CrossWorkspaceTicketsPage from "./pages/CrossWorkspaceTicketsPage";
import SupportTicketsBoardPage from "./pages/SupportTicketsBoardPage";
import PlannerAgilePlanPage from "./pages/PlannerAgilePlanPage";
import PlannerCatalogPage from "./pages/PlannerCatalogPage";
import PlannerClientSummaryPage from "./pages/PlannerClientSummaryPage";
import PlannerOffsiteLinksPage from "./pages/PlannerOffsiteLinksPage";
import PlannerFeedbackPage from "./pages/PlannerFeedbackPage";
import PlannerGroupsPage from "./pages/PlannerGroupsPage";
import PlannerApprovalsPage from "./pages/PlannerApprovalsPage";
import InternalPreviewPage from "./pages/InternalPreviewPage";
import TechnicalAuditPage from "./pages/TechnicalAuditPage";
import GbpRecommendationsPage from "@/pages/GbpRecommendationsPage";
import SerpPreviewPage from "./pages/SerpPreviewPage";
import CompetitorComparisonPage from "./pages/CompetitorComparisonPage";
import CoreWebVitalsPage from "./pages/CoreWebVitalsPage";
import KeywordResearchPage from "./pages/seo/KeywordResearchPage";
import InternalPagesIndexPage from "./pages/seo/InternalPagesIndexPage";
import LLMBenchmarksPage from "./pages/admin/LLMBenchmarksPage";
import ResearchCoveragePage from "./pages/admin/ResearchCoveragePage";
import GoogleAdsOpsPage from "./pages/GoogleAdsOpsPage";
import GoogleAdsImageAssetsPage from "./pages/GoogleAdsImageAssetsPage";
import LandingPagesPage from "./pages/LandingPagesPage";
import PageBuilderOverviewPage from "./pages/PageBuilderOverviewPage";
import AllClientsPage from "./pages/AllClientsPage";
import ClientDetailPage from "./pages/ClientDetailPage";
import MccOverviewPage from "./pages/MccOverviewPage";
import WhatConvertsLeadsPage from "./pages/WhatConvertsLeadsPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import AdminInvitePage from "./pages/AdminInvitePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import CentralChatPage from "./pages/CentralChatPage";
import ClientDashboardPage from "./pages/ClientDashboardPage";
import { canAccessInternalAdsTools } from "./lib/adsToolsAccess";
import { useWorkspaceStore } from "./stores/useWorkspaceStore";
import { useTenantStore } from "./stores/useTenantStore";

const ADS_TOOLS_TAB_ROUTES: Record<string, string> = {
	"keyword-research": "keyword-research",
	campaigns: "campaigns",
	"account-audit": "account-audit",
	audit: "account-audit",
	// CORE-D-01: `weekly` and `weekly-management` are historical aliases for
	// the renamed Client Dashboard. Kept so old bookmarks and Slack links
	// resolve; the route redirect below routes them to the new slug.
	weekly: "client-dashboard",
	"weekly-management": "client-dashboard",
	"client-dashboard": "client-dashboard",
	client: "client-dashboard",
	dashboard: "client-dashboard",
	"search-terms": "search-terms",
	negatives: "search-terms",
	// CORE-PF-01 / PF-02: `monthly` / `monthly-recaps` / `kpi-diagnostics`
	// are historical aliases for the merged Performance page.
	monthly: "performance",
	"monthly-recaps": "performance",
	"kpi-diagnostics": "performance",
	performance: "performance",
	kpi: "performance",
	budgets: "budgets",
	// CORE-K-01: Cluster Refresh folds into Keyword Research as the
	// `refresh` subtab. Historical `?tab=cluster-refresh` lands on the
	// Keyword Research page; the explicit `/ads-tools/cluster-refresh`
	// route below carries the subtab param through the redirect.
	"cluster-refresh": "keyword-research",
	competitive: "competitive",
	// CORE-P-01 / P-02: `placement-review` is a historical alias — the
	// Placement Review surface lives inside the Performance Max tab now.
	pmax: "pmax",
	"performance-max": "pmax",
	"placement-review": "pmax",
};

function LegacyEditorRedirect() {
	const { contentId } = useParams();
	return <Navigate to={`/editor/blog/${encodeURIComponent(contentId ?? "")}`} replace />;
}

function AdsToolsOpsRedirect() {
	const location = useLocation();
	const params = new URLSearchParams(location.search);
	const section = ADS_TOOLS_TAB_ROUTES[params.get("tab") ?? ""] ?? "keyword-research";
	params.set("platform", "google-ads");
	params.delete("tab");
	if (section !== "keyword-research") {
		params.delete("research_tab");
		params.delete("run");
	}
	return <Navigate to={`/ads-tools/${section}?${params.toString()}`} replace />;
}

function GoogleAdsKeywordResearchRedirect() {
	const location = useLocation();
	const params = new URLSearchParams(location.search);
	const legacyTab = params.get("tab");

	params.set("platform", "google-ads");
	params.delete("tab");
	if (legacyTab === "history") {
		params.set("research_tab", "history");
	} else {
		params.delete("research_tab");
		params.delete("run");
	}

	return <Navigate to={`/ads-tools/keyword-research?${params.toString()}`} replace />;
}

const SETTINGS_TAB_DESTINATIONS: Record<string, string> = {
	basic: "/settings",
	location: "/settings",
	advanced: "/settings",
	publishing: "/settings/website",
	indexing: "/settings/website",
	media: "/settings/media",
};

/** Maps retired settings tabs while retaining every non-tab query parameter. */
export function getSettingsTabRedirect(search: string): string | null {
	const params = new URLSearchParams(search);
	if (!params.has("tab")) return null;

	const target = SETTINGS_TAB_DESTINATIONS[params.get("tab") ?? ""] ?? "/settings";
	params.delete("tab");
	const remaining = params.toString();
	return `${target}${remaining ? `?${remaining}` : ""}`;
}

function SettingsRoute() {
	const location = useLocation();
	const destination = getSettingsTabRedirect(location.search);
	return destination ? <Navigate to={destination} replace /> : <ClientSettingsLayout />;
}

function AdsToolsAccessRequired() {
	return (
		<div className="mx-auto flex max-w-2xl flex-col items-center justify-center px-4 py-16 text-center">
			<h2 className="mb-2 text-xl font-semibold">Ads Tools Access Required</h2>
			<p className="text-muted-foreground">
				This operator console is restricted to AgileCo Ads operators and platform admins.
			</p>
		</div>
	);
}

function InternalAdsToolsRoute() {
	const { userProfile } = useWorkspaceStore();
	const location = useLocation();
	const { section } = useParams();

	if (!canAccessInternalAdsTools(userProfile)) {
		return <AdsToolsAccessRequired />;
	}

	// CORE-M-11.b + M-11.b.2: the internal Current Campaigns tab is fully
	// retired (Q-2 ruling — folded into Management's CampaignDrilldown).
	// Any `?campaign_tab=manage` on /ads-tools/campaigns is a stale
	// bookmark — the internal run-completion flow now navigates to
	// /ads-tools/management?campaign=<id> directly, so nothing inside
	// the app writes that query param anymore. All such URLs redirect to
	// Management.
	if (section === "campaigns") {
		const params = new URLSearchParams(location.search);
		if (params.get("campaign_tab") === "manage") {
			return (
				<Navigate
					to={`/ads-tools/management?platform=google-ads`}
					replace
				/>
			);
		}
	}

	return <GoogleAdsOpsPage />;
}

// Cross-account MCC overview — same operator gate as the single-tenant console.
function MccOverviewRoute() {
	const { userProfile } = useWorkspaceStore();

	if (!canAccessInternalAdsTools(userProfile)) {
		return <AdsToolsAccessRequired />;
	}

	return <MccOverviewPage />;
}

// Combined /dashboard route — reads the current tenant the same way every
// other client-facing page does (useTenantStore.currentTenant, the
// domain-string API contract per the auth bridge pattern) and passes it down
// as the `website` prop ClientDashboardPage expects. Folds the retired
// client-analytics contract into the SEO Overview grammar (v2 combined-
// dashboard design); /analytics now redirects here.
function ClientDashboardRoute() {
	const { currentTenant } = useTenantStore();
	return <ClientDashboardPage website={currentTenant ?? ''} />;
}

// WhatConverts qualified-leads view — same operator gate.
function WhatConvertsRoute() {
	const { userProfile } = useWorkspaceStore();

	if (!canAccessInternalAdsTools(userProfile)) {
		return <AdsToolsAccessRequired />;
	}

	return <WhatConvertsLeadsPage />;
}

// PMax image-asset upload (#11) — same operator gate.
function GoogleAdsImageAssetsRoute() {
	const { userProfile } = useWorkspaceStore();

	if (!canAccessInternalAdsTools(userProfile)) {
		return <AdsToolsAccessRequired />;
	}

	return <GoogleAdsImageAssetsPage />;
}

// Guards AgileCo-internal pages (QA review/checks). Shares the same
// elevated-role OR @agileandco.com OR VITE_DISABLE_AUTH predicate as the
// Ads Tools guard above. ProtectedRoute blocks rendering until userProfile
// is fetched, so this never redirects before the profile is available.
function InternalOnlyRoute({ children }: { children: ReactNode }) {
	const { userProfile } = useWorkspaceStore();

	if (!canAccessInternalAdsTools(userProfile)) {
		return <Navigate to="/dashboard" replace />;
	}

	return <>{children}</>;
}

function App() {
	// Ant Design reads its theme tokens from JS at render time, so it cannot
	// consume `--color-coral` directly. Resolving the computed custom property
	// keeps antd on the same source of truth as Tailwind/shadcn — and re-renders
	// when that property is overridden at runtime (per-client theming, #959).
	const brand = useBrandTokens();
	const { resolvedTheme } = useTheme();

	return (
		<ConfigProvider
			theme={{
				token: {
					colorPrimary: brand.coral,
					borderRadius: 8,
				},
				algorithm:
					resolvedTheme === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
			}}
		>
			{/* Global toast notifications — follow the app's chosen theme */}
			<Toaster
				theme={resolvedTheme}
				position="top-right"
				expand={false}
				richColors
				closeButton
				toastOptions={{
					duration: 4000,
				}}
			/>
			<Router basename={import.meta.env.BASE_URL}>
				<ErrorBoundary>
				<AuthProvider>
				<PrePublishGateProvider>
					<Routes>
						<Route path="/login" element={<LoginPage />} />
						<Route path="/signup" element={<SignupPage />} />
						<Route path="/password-reset" element={<PasswordResetPage />} />
						<Route path="/auth/callback" element={<AuthCallback />} />
						<Route path="/auth/accept-invite" element={<AcceptInvitePage />} />
						<Route path="/auth/admin-invite" element={<AdminInvitePage />} />
						<Route path="/create-workspace" element={<ProtectedRoute><CreateWorkspacePage /></ProtectedRoute>} />
						{/* Client onboarding — 7-phase hub + card. Outside the app shell:
						    a new client has nothing to navigate yet. */}
						<Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
						<Route path="/onboarding/:step" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
						<Route path="/privacy" element={<PrivacyPolicyPage />} />
						<Route path="/" element={<ProtectedRoute><ModernMainLayout /></ProtectedRoute>}>
							<Route index element={<Navigate to="/dashboard" replace />} />
							{/* The combined dashboard is the canonical client home. Retired
							    performance URLs redirect here; detail routes below remain live. */}
							<Route path="overview" element={<Navigate to="/dashboard" replace />} />
							<Route path="performance" element={<Navigate to="/dashboard" replace />} />
							<Route path="ads-performance" element={<Navigate to="/dashboard" replace />} />
							{/* /analytics retired 20260808 — folded into /dashboard (Carl's decision). */}
							<Route path="analytics" element={<Navigate to="/dashboard" replace />} />
							<Route path="dashboard" element={<ClientDashboardRoute />} />
							<Route path="overview/top-pages" element={<TopPagesPage />} />
							<Route path="overview/traffic" element={<TrafficPage />} />
							<Route path="overview/site-health" element={<SiteHealthPage />} />
							<Route path="connect/search-console" element={<ConnectSearchConsolePage />} />
							<Route path="connect/ga4" element={<ConnectGA4Page />} />
							<Route path="chat" element={<CentralChatPage />} />
							<Route path="settings" element={<SettingsRoute />}>
								<Route index element={<BusinessInfoSection />} />
								<Route path="integrations" element={<IntegrationsSection />} />
								<Route path="content" element={<ContentSection />} />
								<Route path="website" element={<WebsiteSection />} />
								<Route path="media" element={<MediaSection />} />
								<Route path="team" element={<TeamSection />} />
							</Route>

							<Route path="brand-bible" element={<BrandBiblePage />} />
							{/* Wizard route retirement (design spec item 6 / wayfinder L7):
								capabilities live on /brand-bible. Old /brand-bible/wizard links
								redirect rather than 404. Shell + Step* components deleted 2026-08-17. */}
							<Route path="brand-bible/wizard" element={<Navigate to="/brand-bible" replace />} />
							{SETTINGS_REDIRECT_ROUTES}
							<Route path="content/blog" element={<Navigate to="/content/planner" replace />} />
							<Route path="content/outline-review/:contentId" element={<OutlineReviewPage />} />
							<Route path="blog/:contentId" element={<BlogDetailPage />} />
							<Route path="content/templates" element={<BlogTemplatesPage />} />
							<Route path="content/landing-pages" element={FEATURE_FLAGS.landingPagesEnabled ? <LandingPagesPage /> : <Navigate to="/dashboard" replace />} />
							<Route path="content/page-builder/overview" element={FEATURE_FLAGS.landingPagesEnabled ? <PageBuilderOverviewPage /> : <Navigate to="/dashboard" replace />} />
							<Route path="content/planner" element={<ContentPlannerPage />} />
							<Route path="content/planner/wizard" element={<ContentPlannerWizardPage />} />
							<Route
								path="content/planner/research/:mapId"
								element={<ClusterResultsPage />}
							/>
							{/* Classic editor retired — permanent redirect to the universal editor */}
							<Route
								path="content/editor/:contentId"
								element={<LegacyEditorRedirect />}
							/>
							<Route
								path="editor/:contentType/:contentId"
								element={<UniversalEditorPage />}
							/>
							<Route path="content/benchmarks" element={<BenchmarkDashboardPage />} />
							<Route path="content/supervised/:sessionId" element={<SectionReviewPage />} />
							<Route path="content/supervised-review/:contentId" element={<SectionReviewPage />} />
							<Route path="content/human-qa" element={<InternalOnlyRoute><HumanQaReviewWorkbenchPage /></InternalOnlyRoute>} />
							<Route path="content/qa" element={<InternalOnlyRoute><QAReviewPage /></InternalOnlyRoute>} />
							<Route path="content/qa-checks" element={<InternalOnlyRoute><QAContentChecksPage /></InternalOnlyRoute>} />
							<Route path="proposals" element={FEATURE_FLAGS.proposalToolEnabled ? <InternalOnlyRoute><ProposalsPage /></InternalOnlyRoute> : <Navigate to="/dashboard" replace />} />
							<Route path="proposals/:proposalId" element={FEATURE_FLAGS.proposalToolEnabled ? <InternalOnlyRoute><ProposalsPage /></InternalOnlyRoute> : <Navigate to="/dashboard" replace />} />
							<Route path="planner" element={<InternalOnlyRoute><PlannerAgilePlanPage /></InternalOnlyRoute>} />
							{/* C2 clients consolidation: the parent Clients section. Old planner/ads
							    URLs redirect below so bookmarks survive. */}
							<Route path="clients" element={<InternalOnlyRoute><AllClientsPage /></InternalOnlyRoute>} />
							<Route path="clients/c/:contractId" element={<InternalOnlyRoute><ClientDetailPage /></InternalOnlyRoute>} />
							<Route path="clients/ads" element={<MccOverviewRoute />} />
							<Route path="clients/groups" element={<InternalOnlyRoute><PlannerGroupsPage /></InternalOnlyRoute>} />
							<Route path="clients/summary" element={<InternalOnlyRoute><PlannerClientSummaryPage /></InternalOnlyRoute>} />
							<Route path="planner/clients" element={<Navigate to="/clients" replace />} />
							{/* /planner/pacing was a duplicate of /ads-tools/budgets; the push queue moved to /ads-tools/management. Redirect keeps bookmarks alive. */}
							<Route path="planner/pacing" element={<Navigate to="/ads-tools/budgets" replace />} />
							<Route path="planner/offsite" element={<InternalOnlyRoute><PlannerOffsiteLinksPage /></InternalOnlyRoute>} />
							<Route path="planner/summary" element={<Navigate to="/clients/summary" replace />} />
							<Route path="planner/feedback" element={<InternalOnlyRoute><PlannerFeedbackPage /></InternalOnlyRoute>} />
							<Route path="planner/groups" element={<Navigate to="/clients/groups" replace />} />
							<Route path="planner/approvals" element={<InternalOnlyRoute><PlannerApprovalsPage /></InternalOnlyRoute>} />
							{/* Staff Preview Features toggle — NOT flag-gated (must be reachable to turn flags on). */}
							<Route path="internal/preview" element={<InternalOnlyRoute><InternalPreviewPage /></InternalOnlyRoute>} />
							<Route path="planner/catalog" element={<InternalOnlyRoute><PlannerCatalogPage /></InternalOnlyRoute>} />
							{/* <Route path="content/calendar" element={<ContentCalendarPage />} /> */}
							<Route path="audit/technical" element={<TechnicalAuditPage />} />
							{/* Site Health → GBP recommendations (Local Falcon). The API is
							    flag-gated by LOCAL_FALCON_ENABLED, so this renders a
							    "not connected" state until a key is configured. */}
							<Route path="seo/gbp" element={<GbpRecommendationsPage />} />
							<Route
								path="audit/technical-issues"
								element={<TechnicalAuditIssuesPage />}
							/>
							<Route
								path="audit/tickets"
								element={<CrossWorkspaceTicketsPage />}
							/>
							{/* The board carries the request form (Add Task), so the
							    standalone page is gone. Redirect rather than 404 --
							    clients have been given /support links. */}
							<Route
								path="support"
								element={<Navigate to="/support/tickets" replace />}
							/>
							<Route
								path="support/tickets"
								element={<SupportTicketsBoardPage />}
							/>
							<Route
								path="comprehensive-site-audit"
								element={<ComprehensiveSiteAuditPage />}
							/>
							<Route
								path="seo/schema-management"
								element={<SchemaManagementPage />}
							/>
							<Route path="seo/serp-preview" element={<SerpPreviewPage />} />
							<Route path="seo/competitor-compare" element={<CompetitorComparisonPage />} />
							<Route path="seo/core-web-vitals" element={<CoreWebVitalsPage />} />

							<Route path="seo/keyword-research" element={<KeywordResearchPage />} />
							<Route path="seo/internal-pages" element={<InternalPagesIndexPage />} />
							<Route path="crews" element={<InternalOnlyRoute><CrewsPage /></InternalOnlyRoute>} />
							<Route path="agents" element={<InternalOnlyRoute><AgentsPage /></InternalOnlyRoute>} />
							<Route path="tasks" element={<InternalOnlyRoute><TasksPage /></InternalOnlyRoute>} />
							<Route path="tools" element={<InternalOnlyRoute><ToolsPage /></InternalOnlyRoute>} />

							{PLATFORM_REDIRECT_ROUTES}
							<Route path="admin/google-ads" element={<AdsToolsOpsRedirect />} />
							<Route path="admin/llm-benchmarks" element={<InternalOnlyRoute><LLMBenchmarksPage /></InternalOnlyRoute>} />
							<Route path="admin/research-coverage" element={<InternalOnlyRoute><ResearchCoveragePage /></InternalOnlyRoute>} />
							<Route path="admin/hermes-vetting" element={<Navigate to="/admin/research-coverage" replace />} />
							<Route path="ads-tools" element={<AdsToolsOpsRedirect />} />
							<Route path="ads-tools/ops" element={<AdsToolsOpsRedirect />} />
							<Route path="ads-tools/overview" element={<Navigate to="/clients/ads" replace />} />
							<Route path="ads-tools/whatconverts" element={<WhatConvertsRoute />} />
							<Route path="ads-tools/image-assets" element={<GoogleAdsImageAssetsRoute />} />
							{/* CORE-D-01: preserve bookmarks and Slack links pointing at the
								old Weekly Management path — they now open the Client Dashboard.
								Redirect must sit BEFORE the :section fallback so the specific
								path wins. */}
							<Route
								path="ads-tools/weekly-management"
								element={<Navigate to="/ads-tools/client-dashboard" replace />}
							/>
							{/* CORE-P-02: Placement Review lives inside the Performance Max
								tab now. Explicit redirect so historical bookmarks resolve
								before the :section catch-all. */}
							<Route
								path="ads-tools/placement-review"
								element={<Navigate to="/ads-tools/pmax" replace />}
							/>
							{/* CORE-K-01: Cluster Refresh is now a subtab of Keyword
								Research (`research_tab=refresh`). Preserve historical
								bookmarks pointing at the standalone route. Redirect
								sits BEFORE the :section catch-all so the specific
								path wins. */}
							<Route
								path="ads-tools/cluster-refresh"
								element={
									<Navigate
										to="/ads-tools/keyword-research?platform=google-ads&research_tab=refresh"
										replace
									/>
								}
							/>
							{/* CORE-PF-01 / PF-02: KPI Diagnostics + Monthly Recaps merged
								into the Performance page. Preserve historical bookmarks. */}
							<Route
								path="ads-tools/kpi-diagnostics"
								element={<Navigate to="/ads-tools/performance" replace />}
							/>
							<Route
								path="ads-tools/monthly-recaps"
								element={<Navigate to="/ads-tools/performance" replace />}
							/>
							<Route path="ads-tools/:section" element={<InternalAdsToolsRoute />} />
							<Route path="google-ads/ops" element={<AdsToolsOpsRedirect />} />
							<Route path="google-ads/keyword-research" element={<GoogleAdsKeywordResearchRedirect />} />
						<Route path="usage" element={<UsagePage />} />
						<Route path="credits" element={<UsageCreditsPage />} />
						<Route path="cost-attribution" element={<CostAttributionPage />} />
						<Route path="run" element={<RunPage />} />
							<Route path="results" element={<ResultsPage />} />
							<Route path="*" element={<Navigate to="/" replace />} />
						</Route>
					</Routes>
				</PrePublishGateProvider>
				</AuthProvider>
				</ErrorBoundary>
			</Router>
		</ConfigProvider>
	);
}

export default App;
